import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { KNOWLEDGE_ARTICLES } from '../data/knowledgeArticles';
import { COMPANY_INFO } from '../data/staticData';
import { imageUrl } from '../lib/images';

export default function KnowledgeDetailPage() {
  const { slug } = useParams();
  const [loadVideo, setLoadVideo] = useState(false);
  const article = KNOWLEDGE_ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center space-y-4">
        <h1 className="text-3xl font-bold text-stone-900">Artiklen blev ikke fundet</h1>
        <p className="text-stone-600">Den efterspurgte artikel findes ikke eller er flyttet.</p>
        <Link
          to="/viden"
          className="inline-block px-5 py-2.5 bg-stone-900 text-white font-bold text-sm uppercase"
        >
          Se alle artikler under Viden
        </Link>
      </div>
    );
  }

  // Parse inline text with bold (**text**) and italic (*text*)
  const parseInline = (str) => {
    const parts = [];
    let remaining = str;
    let key = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
      const italicMatch = remaining.match(/\*(.*?)\*/);

      if (boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index)) {
        if (boldMatch.index > 0) {
          parts.push(remaining.substring(0, boldMatch.index));
        }
        parts.push(
          <strong key={key++} className="font-bold text-stone-900">
            {boldMatch[1]}
          </strong>
        );
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
      } else if (italicMatch) {
        if (italicMatch.index > 0) {
          parts.push(remaining.substring(0, italicMatch.index));
        }
        parts.push(
          <em key={key++} className="italic text-stone-800">
            {italicMatch[1]}
          </em>
        );
        remaining = remaining.substring(italicMatch.index + italicMatch[0].length);
      } else {
        parts.push(remaining);
        break;
      }
    }

    return parts;
  };

  // Parse markdown text into formatted sections
  const renderContent = (text) => {
    const blocks = text.split('\n\n');
    return blocks.map((block, idx) => {
      const trimmed = block.trim();
      if (!trimmed) return null;

      if (trimmed.startsWith('### ')) {
        return (
          <h3 key={idx} className="text-xl sm:text-2xl font-bold text-stone-900 pt-6 pb-2 uppercase tracking-wide">
            {trimmed.replace('### ', '')}
          </h3>
        );
      }
      if (trimmed.startsWith('## ')) {
        return (
          <h2 key={idx} className="text-2xl sm:text-3xl font-extrabold text-stone-900 pt-8 pb-3 uppercase tracking-tight">
            {trimmed.replace('## ', '')}
          </h2>
        );
      }
      if (trimmed.startsWith('# ')) {
        return (
          <h1 key={idx} className="text-3xl sm:text-4xl font-extrabold text-stone-900 pt-4 pb-2 uppercase tracking-tight">
            {trimmed.replace('# ', '')}
          </h1>
        );
      }

      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const items = trimmed.split('\n').map((line) => line.replace(/^[-*]\s*/, '').trim());
        return (
          <ul key={idx} className="list-disc pl-6 space-y-2 text-stone-700 text-base sm:text-lg leading-relaxed my-4">
            {items.map((item, i) => (
              <li key={i}>{parseInline(item)}</li>
            ))}
          </ul>
        );
      }

      return (
        <p key={idx} className="text-stone-700 text-base sm:text-lg leading-relaxed mb-4">
          {parseInline(trimmed)}
        </p>
      );
    });
  };

  return (
    <div className="bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-16 space-y-10">
        
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-stone-500 flex-wrap">
          <Link to="/" className="hover:text-stone-900 transition-colors">Forside</Link>
          <span>/</span>
          <Link to="/viden" className="hover:text-stone-900 transition-colors">Viden</Link>
          <span>/</span>
          <span className="text-stone-900 font-semibold">{article.title}</span>
        </nav>

        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-xs text-stone-500">
            {article.category && (
              <span className="font-bold text-[#BE0F12] uppercase tracking-wider bg-red-50 px-2.5 py-1 border border-red-100">
                {article.category}
              </span>
            )}
            {article.date && <span>Udgivet: {article.date}</span>}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight uppercase leading-tight">
            {article.title}
          </h1>
          {article.meta_description && (
            <p className="text-stone-600 text-lg sm:text-xl leading-relaxed italic border-l-4 border-[#BE0F12] pl-4 py-1">
              {article.meta_description}
            </p>
          )}
        </div>

        {/* Hero Media (Video or Image) */}
        {article.videoId ? (
          <div className="rounded-none overflow-hidden border border-stone-200 shadow-sm bg-stone-900">
            <div className="relative aspect-video w-full">
              {!loadVideo ? (
                <div
                  className="relative w-full h-full cursor-pointer group"
                  onClick={() => setLoadVideo(true)}
                >
                  <img
                    src={`https://i.ytimg.com/vi/${article.videoId}/hqdefault.jpg`}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#BE0F12] text-white flex items-center justify-center text-2xl shadow-xl transform group-hover:scale-110 transition-transform">
                      <i className="fa-solid fa-play ml-1"></i>
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${article.videoId}?autoplay=1`}
                  title={article.title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          </div>
        ) : article.image ? (
          <div className="rounded-none overflow-hidden border border-stone-200 shadow-sm bg-stone-100">
            <img
              src={imageUrl(article.image, 'width=1200,quality=85,format=auto')}
              alt={article.title}
              className="w-full h-auto max-h-[500px] object-cover"
            />
          </div>
        ) : null}

        {/* Lasse Kontakt Boks (kendt fra alle undersider i Dansk Tagbyg) */}
        <div className="p-6 bg-stone-50 border border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img
              src={imageUrl('/_assets/lasse-sidebar.png', 'width=160,quality=85,format=auto')}
              alt="Lasse - Dansk Tagbyg"
              className="w-16 h-16 rounded-full object-cover border-2 border-[#BE0F12] shrink-0 bg-white"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div>
              <p className="font-extrabold text-stone-900 text-lg uppercase">Kontakt mig, Lasse</p>
              <p className="text-stone-600 text-sm">Få rådgivning eller et uforpligtende tilbud på dit tag</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <a
              href="tel:28687450"
              className="px-5 py-2.5 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-xs uppercase flex items-center justify-center gap-2 transition-colors"
            >
              <i className="fa-solid fa-phone"></i>
              <span>Ring: 28 68 74 50</span>
            </a>
            <a
              href="mailto:kontakt@dansktagbyg.dk"
              className="px-5 py-2.5 bg-white border border-stone-300 hover:bg-stone-50 text-stone-900 font-bold text-xs uppercase flex items-center justify-center gap-2 transition-colors"
            >
              <i className="fa-solid fa-envelope"></i>
              <span>Skriv mail</span>
            </a>
          </div>
        </div>

        {/* Article Body */}
        <div className="prose prose-stone max-w-none">
          {renderContent(article.content)}
        </div>

        {/* Få et uforpligtende tilbud via vores prisberegner CTA boks */}
        <div className="p-8 bg-stone-900 text-white border-l-4 border-[#BE0F12] space-y-4">
          <h3 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight">
            Få et uforpligtende tilbud via vores prisberegner
          </h3>
          <p className="text-stone-300 text-base leading-relaxed">
            Ud fra 4 simple spørgsmål beregner vi med det samme en uforpligtende pris til dig på nyt tag. Hurtigt og nemt!
          </p>
          <div>
            <Link
              to="/prisberegner"
              className="inline-block px-6 py-3 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-sm uppercase tracking-wider transition-colors"
            >
              Prøv prisberegner
            </Link>
          </div>
        </div>

        {/* Review Box */}
        <div className="p-6 bg-stone-50 border border-stone-200 text-center space-y-2">
          <div className="flex items-center justify-center gap-1 text-amber-500 text-base">
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star"></i>
          </div>
          <a
            href="https://www.anmeld-haandvaerker.dk/tagdaekker/dansk-tagbyg-aps"
            target="_blank"
            rel="noreferrer"
            className="block font-black text-lg text-stone-900 hover:text-[#BE0F12] transition-colors"
          >
            4,8 ud af 155 anmeldelser
          </a>
          <p className="text-xs text-stone-500">
            Dansk Tagbyg – Dit professionelle tagfirma med kunder i hele Danmark.
          </p>
        </div>

        {/* Navigation / CTA */}
        <div className="pt-8 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link
            to="/viden"
            className="inline-flex items-center gap-2 text-sm font-bold text-stone-700 hover:text-black uppercase"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i>
            <span>Tilbage til oversigten over Viden</span>
          </Link>
          <div className="flex gap-3">
            <Link
              to="/prisberegner"
              className="px-5 py-2.5 bg-stone-900 hover:bg-black text-white font-bold text-xs uppercase transition-colors"
            >
              Beregn tagpris
            </Link>
            <Link
              to="/kontakt"
              className="px-5 py-2.5 bg-white hover:bg-stone-50 text-stone-900 font-bold text-xs border border-stone-300 transition-colors uppercase"
            >
              Kontakt os
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
